import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Show } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { StyledDropdownOption, StyledRightSlot } from 'components/Dropdown/styles';
import { LowQuotaNotificationBanner } from 'components/quota-components/LowQuotaNotificationBanner';
import { QuotaBadge } from 'components/quota-components/QuotaBadge';
import { Tooltip } from 'components/Tooltip';
import { useLowQuotaNotificationConfig } from 'features/quota/hooks/useLowQuotaNotificationConfig';
import { useQuota } from 'features/quota/hooks/useQuota';
import { useUserStatus } from 'hooks/useUserStatus';

import { useBulkReveal } from '../../useBulkReveal';

import { StyledBulkRevealButton, StyledBulkRevealContainer, StyledDropdown, StyledOptionText } from './styles';

interface IBulkRevealDropdownProps {
  isDisabled?: boolean;
  contactsToRevealCount: number;
  onRevealFinish: VoidFunction;
}

export const BulkRevealDropdown = ({
  isDisabled = false,
  contactsToRevealCount,
  onRevealFinish,
}: IBulkRevealDropdownProps) => {
  const { t } = useTranslation();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { areAllContactsRevealed, isPending, onBulkReveal } = useBulkReveal({
    onRevealFinish,
  });

  const { quota, canAfford, getRevealCost } = useQuota();

  const revealCost = getRevealCost(contactsToRevealCount);
  const isRevealAffordable = canAfford(revealCost);

  const { isNoTouch } = useUserStatus();

  // No-touch users: show banner when operation is unaffordable (ignores isLow threshold)
  // Other users: show banner when quota is low / not enough quota for operation
  const shouldShowBanner = isNoTouch ? !isRevealAffordable : quota.isLow || !isRevealAffordable;

  const lowQuotaNotificationConfig = useLowQuotaNotificationConfig();

  const handleClose = (): void => {
    setIsDropdownOpen(false);
  };

  const handleOpen = (): void => {
    setIsDropdownOpen(true);
  };

  const handleRevealClick = (): void => {
    if (!isRevealAffordable) {
      return;
    }

    onBulkReveal();
    handleClose();
  };

  return (
    <StyledBulkRevealContainer>
      <Tooltip enabled={areAllContactsRevealed} content={t('contact_card.revealed_all')} placement="top" minWidth={230}>
        <StyledBulkRevealButton
          variant="flat"
          data-automation="ContactReveal"
          disabled={isDisabled || isPending || areAllContactsRevealed}
          isActive={isDropdownOpen}
          onClick={handleOpen}
        >
          {t('contact_card.reveal')}
        </StyledBulkRevealButton>
      </Tooltip>

      <StyledDropdown isOpened={isDropdownOpen} onClose={handleClose} placement="left" minWidth={260}>
        <StyledDropdownOption
          data-automation="BulkRevealDropdownOption"
          isDisabled={!isRevealAffordable}
          onClick={handleRevealClick}
        >
          <Show size={24} color={colorsPalettes.carbon[200]} />
          <StyledOptionText>{t('contact_card.reveal_contact.plural')}</StyledOptionText>

          <StyledRightSlot>
            <QuotaBadge type="count" value={revealCost} variant={!isRevealAffordable ? 'warning' : 'info'} />
          </StyledRightSlot>
        </StyledDropdownOption>

        <LowQuotaNotificationBanner
          isSuccess={lowQuotaNotificationConfig.isSuccess}
          isVisible={shouldShowBanner}
          title={lowQuotaNotificationConfig.title}
          description={lowQuotaNotificationConfig.description}
          buttonText={lowQuotaNotificationConfig.buttonText}
          onButtonClick={lowQuotaNotificationConfig.onButtonClick}
          isLoading={lowQuotaNotificationConfig.isLoading}
        />
      </StyledDropdown>
    </StyledBulkRevealContainer>
  );
};
